import type { CampaignViewDto } from '../../../types/campaign'
import { useState, useEffect, useMemo } from 'preact/hooks'
import type { DashboardRange } from '@/types/dashboard'
import { DASHBOARD_RANGE_OPTIONS } from '@/types/dashboard'
import { useDashboardTiles } from '@/hooks/useDashboardTiles'
import { campaignApi } from '@/api/campaign'
import { useAnalyticsView } from '@/hooks/useAnalyticsView'
import { canUseMockData } from '@/constants/env'
import { TotalLeadsStatTile } from '@/components/dashboard/TotalLeadsStatTile'
import { CompaniesEngagedStatTile } from '@/components/dashboard/CompaniesEngagedStatTile'
import { ActiveWebsStatTile } from '@/components/dashboard/ActiveWebsStatTile'
import { LandingPageViewsStatTile } from '@/components/dashboard/LandingPageViewsStatTile'
import { UniqueLandingPageViewsStatTile } from '@/components/dashboard/UniqueLandingPageViewsStatTile'
import { ConversionsStatTile } from '@/components/dashboard/ConversionsStatTile'
import { VisitorFunnelStatTile } from '@/components/dashboard/VisitorFunnelStatTile'
import { NewestConversionsTile } from '@/components/dashboard/NewestConversionsTile'
import { HottestCompaniesTile } from '@/components/dashboard/HottestCompaniesTile'
import { TopPerformingWebsTile } from '@/components/dashboard/TopPerformingWebsTile'
import { HottestLeadsTile } from '@/components/dashboard/HottestLeadsTile'
import { useAIChatContextUpdater } from '@/hooks'
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat'
import { PipelineGeneratedStatTile } from '@/components/dashboard/PipelineGeneratedStatTile'
import { ClosedWonStatTile } from '@/components/dashboard/ClosedWonStatTile'
import { useCampaignDealAnalytics } from '@/hooks/useHubSpotDealAnalytics'
import { useCompany } from '@/hooks/useCompany'
import { timeFromNowVerbose } from '@/utils/time'

interface Props {
  campaign: CampaignViewDto
}

export function CampaignAnalyticsTab({ campaign }: Props) {
  const [range, setRange] = useState<DashboardRange>('AllTime')
  const [useMock, setUseMock] = useState(canUseMockData())
  const showMockToggle = canUseMockData()
  const workspaceId = campaign.WorkspaceId
  const campaignId = campaign.Id
  const { companyId } = useCompany()
  const { updateContext } = useAIChatContextUpdater()

  // Visitor analytics for this campaign. Fetched once here and passed to the three
  // analytics tiles (the endpoint returns all metrics in one AnalyticsViewDto).
  const analyticsFetcher = useMemo(
    () => (workspaceId && campaignId ? () => campaignApi.getAnalytics(workspaceId, campaignId, range, useMock) : null),
    [workspaceId, campaignId, range, useMock],
  )
  const { data: analytics, loading: analyticsLoading, error: analyticsError } = useAnalyticsView(analyticsFetcher)

  // The window each figure covers, captioned on the tiles so a number can be read — or quoted —
  // without hunting for the range control at the top of the page.
  const rangeLabel = DASHBOARD_RANGE_OPTIONS.find(option => option.value === range)?.label ?? range

  // Every campaign tile below comes from this one request — the same endpoint the workspace
  // dashboard uses, narrowed by campaignId.
  const { tiles, sections, loading: tilesLoading, refreshing: tilesRefreshing, error: tilesError, refresh: refreshTiles, sectionFailed } = useDashboardTiles({
    workspaceId,
    range,
    campaignId,
    mock: useMock,
  })

  // The bundle is cached server-side, so the page cannot claim to be live.
  const generatedRelative = tiles ? timeFromNowVerbose(tiles.GeneratedAtUtc) : null

  // Deal attribution for this campaign (audience-driven: the companies on the campaign's target list).
  const dealAnalytics = useCampaignDealAnalytics(workspaceId, campaignId)

  // Update AI context for campaign analytics
  useEffect(() => {
    updateContext({
      campaignId,
      location: ChatLocations.CampaignAnalytics,
      entityType: ChatEntityTypes.Campaign,
      entityId: campaignId,
      viewMode: ChatViewModes.View,
      availableFields: [
        { name: 'dateRange', displayName: 'Date Range', fieldType: 'select', currentValue: range, isEditable: false, isFocused: false },
      ],
      availableActions: [],
    })
  }, [workspaceId, campaignId, range, updateContext])

  return (
    <div className="p-6">
      <div className="mb-8">
        <div className="flex items-center gap-4 justify-between">
          <div className="flex flex-col gap-2">
            <h2 className="text-2xl font-semibold text-slate-900 dark:text-white">Campaign Analytics</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300">
              {generatedRelative ? `Generated: ${generatedRelative}` : ' '}
            </p>
          </div>
          <div className="flex items-center gap-6">
            {showMockToggle && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-slate-600 dark:text-slate-300">Use Mock Data</span>
                <button
                  type="button"
                  onClick={() => setUseMock(m => !m)}
                  aria-pressed={useMock}
                  aria-label="Toggle mock data"
                  className={`relative inline-flex h-5 w-9 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 dark:focus:ring-offset-slate-800 ${useMock ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white dark:bg-slate-200 shadow transform transition-transform duration-200 ${useMock ? 'translate-x-4' : ''}`}
                  />
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-slate-600 dark:text-slate-300">Range</label>
              <select
                value={range}
                onChange={e => setRange(e.currentTarget.value as DashboardRange)}
                className="select-range transition-default"
              >
                {DASHBOARD_RANGE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      {tilesError && (
        <div
          role="alert"
          className="mb-6 flex items-center justify-between gap-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/40 dark:bg-red-900/20"
        >
          <div className="min-w-0">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">Couldn't load the dashboard</p>
            <p className="text-2xs text-red-600/80 dark:text-red-400/80 break-words">{tilesError}</p>
          </div>
          <button
            type="button"
            onClick={refreshTiles}
            className="shrink-0 rounded-md border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-900/40"
          >
            Retry
          </button>
        </div>
      )}
      <div className={tilesRefreshing ? "opacity-60 transition-opacity pointer-events-none" : "transition-opacity"}>
      <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3">
        {/* Lead & engagement metrics */}
        <TotalLeadsStatTile rangeLabel={rangeLabel} count={sections?.TotalLeadsGenerated} loading={tilesLoading} failed={sectionFailed('TotalLeadsGenerated')} />
        <CompaniesEngagedStatTile rangeLabel={rangeLabel} count={sections?.CompaniesEngaged} loading={tilesLoading} failed={sectionFailed('CompaniesEngaged')} />
        <ActiveWebsStatTile rangeLabel="Right now" badgeTitle="Webs in this campaign" count={sections?.ActiveWebCount} loading={tilesLoading} failed={sectionFailed('ActiveWebCount')} />
        {/* Deal attribution — audience-driven: deals from the companies on this campaign's target list */}
        <PipelineGeneratedStatTile data={dealAnalytics.data} loading={dealAnalytics.loading} error={dealAnalytics.error} />
        <ClosedWonStatTile data={dealAnalytics.data} loading={dealAnalytics.loading} error={dealAnalytics.error} />
        {/* Visitor analytics + Lead Goal placeholder */}
        <LandingPageViewsStatTile metric={analytics?.LandingPageViews ?? null} loading={analyticsLoading} error={analyticsError} />
        <UniqueLandingPageViewsStatTile metric={analytics?.UniqueLandingPageViews ?? null} loading={analyticsLoading} error={analyticsError} />
        <ConversionsStatTile metric={analytics?.Conversions ?? null} loading={analyticsLoading} error={analyticsError} />
        <VisitorFunnelStatTile analytics={analytics} loading={analyticsLoading} error={analyticsError} />
      </div>
      {/* Tall tiles row 1 */}
      <div className="grid gap-5 grid-cols-1 md:grid-cols-2 mt-8">
        <NewestConversionsTile conversions={sections?.NewestConversions} clientCompanyId={companyId ?? ''} workspaceId={workspaceId} campaignId={campaignId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('NewestConversions')} mock={useMock} />
        <HottestCompaniesTile companies={sections?.HottestCompanies} workspaceId={workspaceId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('HottestCompanies')} mock={useMock} />
      </div>
      {/* Tall tiles row 2 */}
      <div className="grid gap-5 grid-cols-1 md:grid-cols-2 mt-8">
        <TopPerformingWebsTile webs={sections?.TopPerformingWebs} workspaceId={workspaceId} campaignId={campaignId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('TopPerformingWebs')} mock={useMock} />
        <HottestLeadsTile leads={sections?.HottestLeads} clientCompanyId={companyId ?? ''} workspaceId={workspaceId} campaignId={campaignId} className="min-h-[320px]" loading={tilesLoading} failed={sectionFailed('HottestLeads')} mock={useMock} />
      </div>
      </div>
    </div>
  )
}
